import React, { useState, useEffect } from 'react';
import * as stakeholderApprovalService from '../services/stakeholderApprovalService';
import type { StakeholderApproval, ApprovalSummary } from '../services/stakeholderApprovalService';

interface ProjectApprovalModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  token: string;
  isStakeholder: boolean;
  currentUserId: string;
  onApprovalUpdated: () => void;
}

export function ProjectApprovalModal({
  isOpen,
  onClose,
  projectId,
  token,
  isStakeholder,
  currentUserId,
  onApprovalUpdated
}: ProjectApprovalModalProps) {
  const [myApprovals, setMyApprovals] = useState<StakeholderApproval[]>([]);
  const [summary, setSummary] = useState<ApprovalSummary | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [approvingId, setApprovingId] = useState<string | null>(null);
  const [initialsInput, setInitialsInput] = useState<Record<string, string>>({});
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && projectId && token) {
      loadData();
    }
  }, [isOpen, projectId, token]);

  const loadData = async () => {
    setIsLoading(true);
    setError('');
    try {
      if (isStakeholder) {
        const approvals = await stakeholderApprovalService.getMyApprovals(projectId, token);
        setMyApprovals(approvals);
      } else {
        const data = await stakeholderApprovalService.getProjectApprovals(projectId, token);
        setSummary(data);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load approvals');
    } finally {
      setIsLoading(false);
    }
  };

  const handleApprove = async (approvalId: string) => {
    const initials = (initialsInput[approvalId] || '').trim();
    if (!initials) {
      setError('Please enter your initials');
      return;
    }
    if (initials.length > 10) {
      setError('Initials must be 10 characters or fewer');
      return;
    }

    setApprovingId(approvalId);
    setError('');
    try {
      await stakeholderApprovalService.approveLineItem(projectId, approvalId, initials, token);
      await loadData();
      onApprovalUpdated();
      setConfirmingId(null);
      setInitialsInput(prev => ({ ...prev, [approvalId]: '' }));
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to approve item');
    } finally {
      setApprovingId(null);
    }
  };

  if (!isOpen) return null;

  // Group approvals by stakeholder for non-stakeholder view
  const stakeholderGroups: Record<string, { name: string; email: string; approvals: StakeholderApproval[] }> = {};
  if (summary) {
    for (const approval of summary.approvals) {
      if (!stakeholderGroups[approval.user_id]) {
        stakeholderGroups[approval.user_id] = {
          name: `${approval.user_first_name || ''} ${approval.user_last_name || ''}`.trim(),
          email: approval.user_email || '',
          approvals: []
        };
      }
      stakeholderGroups[approval.user_id].approvals.push(approval);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-blue-600 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-white">
                {isStakeholder ? 'Project Approval' : 'Stakeholder Approvals'}
              </h2>
              <p className="mt-1 text-sm text-blue-100">
                {isStakeholder ? 'Review and approve project deliverables' : 'View stakeholder sign-off status'}
              </p>
            </div>
            <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md">
              <div className="flex">
                <svg className="w-5 h-5 text-red-600 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-sm text-gray-600">Loading approvals...</p>
            </div>
          ) : isStakeholder ? (
            /* Stakeholder View */
            <div className="space-y-4">
              {myApprovals.length === 0 ? (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No approval items</h3>
                  <p className="mt-1 text-sm text-gray-500">No items require your approval at this time.</p>
                </div>
              ) : (
                myApprovals.map((approval) => (
                  <div
                    key={approval.id}
                    className={`border rounded-lg p-4 ${
                      approval.is_approved
                        ? 'border-green-200 bg-green-50'
                        : 'border-blue-200 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {approval.is_approved ? (
                          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                          </div>
                        ) : (
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                            <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          </div>
                        )}
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900">{approval.line_item_label}</h4>
                          {approval.is_approved ? (
                            <div className="flex items-center gap-2 mt-1">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Approved
                              </span>
                              <span className="text-xs text-gray-500">
                                {approval.approver_initials} &middot; {new Date(approval.approved_at!).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                              </span>
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500 mt-0.5">Pending approval</p>
                          )}
                        </div>
                      </div>

                      {!approval.is_approved && (
                        <div>
                          {confirmingId === approval.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                placeholder="Initials"
                                value={initialsInput[approval.id] || ''}
                                onChange={(e) => setInitialsInput(prev => ({ ...prev, [approval.id]: e.target.value }))}
                                maxLength={10}
                                className="w-20 px-2 py-1 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500"
                                autoFocus
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') handleApprove(approval.id);
                                  if (e.key === 'Escape') setConfirmingId(null);
                                }}
                              />
                              <button
                                onClick={() => handleApprove(approval.id)}
                                disabled={approvingId === approval.id}
                                className="px-3 py-1 text-sm font-medium text-white bg-blue-600 rounded-full hover:bg-blue-700 disabled:opacity-50 transition-colors"
                              >
                                {approvingId === approval.id ? 'Saving...' : 'Confirm'}
                              </button>
                              <button
                                onClick={() => setConfirmingId(null)}
                                className="px-3 py-1 text-sm border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setConfirmingId(approval.id)}
                              className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-full hover:bg-blue-700 transition-colors"
                            >
                              Approve
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Non-Stakeholder (PM/Admin) View */
            <div className="space-y-6">
              {summary && summary.total_stakeholders > 0 ? (
                <>
                  {/* Summary header */}
                  <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-700">
                        {summary.fully_approved_stakeholders}/{summary.total_stakeholders}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {summary.fully_approved_stakeholders} of {summary.total_stakeholders} stakeholder{summary.total_stakeholders !== 1 ? 's' : ''} fully approved
                      </p>
                      <p className="text-xs text-gray-500">
                        {summary.fully_approved_stakeholders === summary.total_stakeholders
                          ? 'All stakeholders have signed off'
                          : 'Waiting for remaining stakeholder approvals'}
                      </p>
                    </div>
                  </div>

                  {/* Per-stakeholder tables */}
                  {Object.entries(stakeholderGroups).map(([userId, group]) => {
                    const allApproved = group.approvals.every(a => a.is_approved);
                    return (
                      <div key={userId} className={`border rounded-lg overflow-hidden ${
                        allApproved ? 'border-green-200' : 'border-blue-200'
                      }`}>
                        <div className={`px-4 py-3 flex items-center justify-between ${
                          allApproved ? 'bg-green-50' : 'bg-blue-50'
                        }`}>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-900">{group.name}</span>
                            <span className="text-xs text-gray-500">{group.email}</span>
                          </div>
                          {allApproved ? (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                              Fully Approved
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Pending
                            </span>
                          )}
                        </div>
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-gray-200">
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Item</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Status</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Initials</th>
                              <th className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider px-4 py-2">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {group.approvals.map((approval) => (
                              <tr key={approval.id}>
                                <td className="px-4 py-2.5 text-sm text-gray-900">{approval.line_item_label}</td>
                                <td className="px-4 py-2.5">
                                  {approval.is_approved ? (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                      Approved
                                    </span>
                                  ) : (
                                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                      Pending
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2.5 text-sm text-gray-600">{approval.approver_initials || '—'}</td>
                                <td className="px-4 py-2.5 text-sm text-gray-500">
                                  {approval.approved_at
                                    ? new Date(approval.approved_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                                    : '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    );
                  })}
                </>
              ) : (
                <div className="text-center py-8">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No stakeholder approvals</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    No stakeholders have been assigned to this project yet, or none have opened the approval section.
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-100 text-gray-700 rounded-full hover:bg-gray-200 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
